/**
 * /academy/attempt/:id — the sitting « comme à l'examen » (référentiel §5), driven by the server
 * state: a reload resumes at the same place, the time left comes from the server.
 */

import { ApiError, useAcademyAttemptsRetrieve } from "@agencyos/api-client";
import { Button, Skeleton } from "@agencyos/ui-web";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router-dom";

import { CeNavigator } from "./CeNavigator";
import { ExamFrame, ExamHeadTool, formatSeconds, useClockTotal } from "./ExamFrame";
import { MethodLink } from "./ielts/MethodLink";
import { McqSection } from "./McqSection";
import { SpeakingSection } from "./SpeakingSection";
import { WritingSection } from "./WritingSection";
import { useAttemptInfo, useCountdown } from "./shared";
import { isSkill, parseState } from "./types";
import { SkillIcon } from "./ui";
import { useAcademyPaths } from "./paths";

/** The browser « back » button does not leave the sitting: a warning is shown instead. */
function useNoBackNavigation(onBack: () => void): void {
  const callback = useRef(onBack);
  useEffect(() => {
    callback.current = onBack;
  }, [onBack]);
  useEffect(() => {
    window.history.pushState({ academyGuard: true }, "");
    const onPop = () => {
      window.history.pushState({ academyGuard: true }, "");
      callback.current();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
}

export function PlayerPage() {
  const { t } = useTranslation();
  const paths = useAcademyPaths();
  const { id = "" } = useParams();
  const query = useAcademyAttemptsRetrieve(id, { query: { retry: 1, staleTime: 0, refetchOnWindowFocus: true } });
  const { kind, mode } = useAttemptInfo(id);
  const [backWarning, setBackWarning] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const state = useMemo(
    () => (query.data?.status === 200 ? parseState(query.data.data) : null),
    [query.data],
  );
  const refresh = useCallback(async () => {
    await query.refetch();
  }, [query]);
  const left = useCountdown(state?.remaining ?? 0, state ? query.dataUpdatedAt : 0, () => void refresh());
  useNoBackNavigation(() => setBackWarning(true));
  const clockTotal = useClockTotal(`${id}.${state?.section ?? ""}`, state?.remaining ?? 0, formatSeconds(query.data?.data, kind));

  // A new section: move the focus to its title (screen readers announce it).
  const section = state?.section ?? "";
  useEffect(() => {
    if (section) heading.current?.focus({ preventScroll: true });
  }, [section]);

  if (query.isLoading) {
    return (
      <div className="ac-player ac-player--loading">
        <Skeleton style={{ height: 64 }} />
        <Skeleton style={{ height: 360 }} />
      </div>
    );
  }
  if (query.error instanceof ApiError && query.error.status === 404) return <Navigate to={paths.home} replace />;
  if (!state) {
    return (
      <div className="ac-surface ac-card ac-card--center">
        <p className="ko">{t("academy.player.networkError")}</p>
        <Button onClick={() => void refresh()}>{t("common.retry")}</Button>
      </div>
    );
  }
  if (state.status !== "in_progress") return <Navigate to={paths.results(id)} replace />;

  const training = mode === "training";
  const sectionIndex = state.skills.indexOf(state.section || "co");

  return (
    <ExamFrame
      className={`ac-player--${state.section || "co"}`}
      icon={state.section && isSkill(state.section) ? <SkillIcon skill={state.section} size={18} /> : null}
      eyebrow={`${kind ? t(`academy.kind.${kind}`) : ""}${
        state.skills.length > 1 ? ` · ${t("academy.player.sectionOf", { n: sectionIndex + 1, total: state.skills.length })}` : ""
      }`}
      title={state.section ? t(`academy.skill.${state.section}`) : ""}
      headingRef={heading}
      training={training}
      seconds={left}
      clockTotal={clockTotal}
      steps={state.skills}
      stepIndex={sectionIndex}
      quitNote={state.section === "co" && !state.navigable ? t("examFrame.noteListening") : state.section === "eo" ? t("examFrame.noteRecording") : undefined}
      footnote={t("academy.player.saved")}
    >
      {state.section && (
        <ExamHeadTool>
          <MethodLink exam="tcf_canada" skill={state.section} type={state.question?.kind} newTab compact />
        </ExamHeadTool>
      )}
      {backWarning && (
        <p className="ac-note ac-note--warning" role="alert">
          {t("academy.player.noBack")}{" "}
          <button type="button" className="ac-text-button" onClick={() => setBackWarning(false)}>
            {t("common.close")}
          </button>
        </p>
      )}

      {(state.section === "co" || state.section === "ce") &&
        (state.navigable ? (
          <CeNavigator key={`nav-${id}-${state.section}`} attemptId={id} state={state} training={training} onChanged={refresh} />
        ) : (
          <McqSection attemptId={id} state={state} training={training} onChanged={refresh} />
        ))}
      {state.section === "ee" && <WritingSection key={`ee-${id}`} attemptId={id} state={state} onChanged={refresh} />}
      {state.section === "eo" && <SpeakingSection key={`eo-${id}`} attemptId={id} state={state} onChanged={refresh} />}
    </ExamFrame>
  );
}
